import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import Header from '../components/layout/Header';
import FieldMapper from '../components/forms/FieldMapper';
import LeadPreview from '../components/forms/LeadPreview';
import dayjs from 'dayjs';

const STATUS_META = {
    processed: { label: 'Lead creado', tone: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
    received:  { label: 'En proceso',  tone: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
    skipped:   { label: 'Omitido',     tone: 'bg-slate-100 text-slate-600 dark:bg-slate-700/40 dark:text-slate-300' },
    failed:    { label: 'Falló',       tone: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

export default function FormsPage() {
    const qc = useQueryClient();
    const [selectedId, setSelectedId] = useState(null);
    const [newName, setNewName] = useState('');
    const [draftMap, setDraftMap] = useState({});
    const [copied, setCopied] = useState(false);

    const { data: listResp } = useQuery({
        queryKey: ['form-connections'],
        queryFn: () => axios.get('/api/form-connections'),
    });
    const connections = listResp?.data || [];

    const { data: detailResp } = useQuery({
        queryKey: ['form-connection', selectedId],
        queryFn: () => axios.get(`/api/form-connections/${selectedId}`),
        enabled: Boolean(selectedId),
    });
    const detail = detailResp?.data;

    // El mapeo se edita en local y se guarda a mano: así se puede probar la
    // vista previa sin que cada cambio empiece a crear leads de verdad.
    useEffect(() => {
        if (!detail) return;
        const saved = detail.field_map && !Array.isArray(detail.field_map) ? detail.field_map : {};
        const suggested = detail.suggestions && !Array.isArray(detail.suggestions) ? detail.suggestions : {};
        setDraftMap(Object.keys(saved).length > 0 ? saved : suggested);
    }, [detail]);

    const { data: previewResp, isFetching: previewLoading } = useQuery({
        queryKey: ['form-preview', selectedId, draftMap],
        queryFn: () => axios.post(`/api/form-connections/${selectedId}/preview`, { field_map: draftMap }),
        enabled: Boolean(selectedId) && detail?.has_sample && Object.keys(draftMap).length > 0,
    });

    const { data: subsResp } = useQuery({
        queryKey: ['form-submissions', selectedId],
        queryFn: () => axios.get(`/api/form-connections/${selectedId}/submissions`),
        enabled: Boolean(selectedId),
    });
    const submissions = subsResp?.data?.data || [];

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ['form-connections'] });
        qc.invalidateQueries({ queryKey: ['form-connection', selectedId] });
        qc.invalidateQueries({ queryKey: ['form-submissions', selectedId] });
    };

    const create = useMutation({
        mutationFn: () => axios.post('/api/form-connections', { name: newName.trim() }),
        onSuccess: ({ data }) => {
            setNewName('');
            setSelectedId(data.id);
            qc.invalidateQueries({ queryKey: ['form-connections'] });
        },
    });

    const update = useMutation({
        mutationFn: (payload) => axios.put(`/api/form-connections/${selectedId}`, payload),
        onSuccess: refresh,
    });

    const createCustomField = useMutation({
        mutationFn: ({ path, name }) =>
            axios.post(`/api/form-connections/${selectedId}/custom-fields`, { fields: [{ path, name }] }),
        onSuccess: ({ data }) => {
            setDraftMap((m) => ({ ...m, ...data.field_map }));
            qc.invalidateQueries({ queryKey: ['form-connection', selectedId] });
        },
    });

    const reprocess = useMutation({
        mutationFn: () => axios.post(`/api/form-connections/${selectedId}/reprocess`),
        onSuccess: refresh,
    });

    const regenerate = useMutation({
        mutationFn: () => axios.post(`/api/form-connections/${selectedId}/regenerate-token`),
        onSuccess: refresh,
    });

    const dirty = useMemo(
        () => JSON.stringify(draftMap) !== JSON.stringify(detail?.field_map || {}),
        [draftMap, detail],
    );

    const copyUrl = () => {
        navigator.clipboard?.writeText(detail.webhook_url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <Header title="Formularios web" />

            <div className="flex-1 flex overflow-hidden">
                {/* Lista de conexiones */}
                <aside className="w-72 shrink-0 border-r border-gray-200 dark:border-gray-800 flex flex-col">
                    <div className="p-3 border-b border-gray-200 dark:border-gray-800 flex gap-1">
                        <input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && newName.trim() && create.mutate()}
                            placeholder="Nombre del formulario"
                            className="flex-1 min-w-0 text-xs p-2 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                        />
                        <button
                            onClick={() => newName.trim() && create.mutate()}
                            disabled={!newName.trim() || create.isPending}
                            className="shrink-0 px-2 rounded-lg bg-primary text-white disabled:opacity-40"
                            title="Crear conexión"
                        >
                            <span className="material-symbols-outlined text-lg">add</span>
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {connections.length === 0 && (
                            <p className="p-4 text-xs text-gray-400">
                                Creá una conexión por cada formulario de tu web.
                            </p>
                        )}
                        {connections.map((c) => (
                            <button
                                key={c.id}
                                onClick={() => setSelectedId(c.id)}
                                className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-800 ${
                                    selectedId === c.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                                        c.is_ready ? 'bg-emerald-500' : 'bg-amber-500'
                                    }`} />
                                    <p className="text-sm font-medium dark:text-white truncate">{c.name}</p>
                                </div>
                                <p className="text-xs text-gray-400 mt-0.5">
                                    {c.is_ready
                                        ? `${c.leads_count ?? 0} leads de ${c.submissions_count ?? 0} envíos`
                                        : 'Falta configurar el mapeo'}
                                </p>
                            </button>
                        ))}
                    </div>
                </aside>

                {!detail ? (
                    <div className="flex-1 flex items-center justify-center text-sm text-gray-400">
                        Elegí un formulario o creá uno nuevo.
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {/* URL del webhook */}
                        <section className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0">
                                    <h2 className="text-sm font-semibold dark:text-white">{detail.name}</h2>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        Pegá esta URL en el webhook del formulario (en Elementor:
                                        Acciones tras enviar → Webhook).
                                    </p>
                                </div>
                                <label className="flex items-center gap-2 text-xs shrink-0">
                                    <input
                                        type="checkbox"
                                        checked={detail.is_active}
                                        onChange={(e) => update.mutate({ is_active: e.target.checked })}
                                        className="rounded"
                                    />
                                    <span className="dark:text-white">Activa</span>
                                </label>
                            </div>

                            <div className="mt-3 flex gap-2">
                                <code className="flex-1 min-w-0 text-xs p-2.5 bg-gray-50 dark:bg-gray-900/50 rounded-lg break-all dark:text-gray-300">
                                    {detail.webhook_url}
                                </code>
                                <button onClick={copyUrl}
                                    className="shrink-0 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs dark:text-white">
                                    {copied ? '¡Copiada!' : 'Copiar'}
                                </button>
                            </div>

                            <button
                                onClick={() => {
                                    if (confirm('Se generará una URL nueva y la actual dejará de funcionar. ¿Seguir?')) {
                                        regenerate.mutate();
                                    }
                                }}
                                className="mt-2 text-xs text-gray-400 hover:text-red-500"
                            >
                                Regenerar la URL (si se filtró)
                            </button>
                        </section>

                        {/* Mapeo + vista previa */}
                        <section className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                            <div className="xl:col-span-2 space-y-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-semibold dark:text-white">Mapeo de campos</h3>
                                    {dirty && (
                                        <button
                                            onClick={() => update.mutate({ field_map: draftMap })}
                                            disabled={update.isPending}
                                            className="px-3 py-1.5 rounded-lg bg-primary text-white text-xs font-medium disabled:opacity-50"
                                        >
                                            Guardar mapeo
                                        </button>
                                    )}
                                </div>

                                <FieldMapper
                                    detected={detail.detected_fields || []}
                                    fieldMap={draftMap}
                                    targets={detail.targets || {}}
                                    customFields={detail.custom_fields || []}
                                    onChange={setDraftMap}
                                    onCreateCustomField={(path, name) =>
                                        createCustomField.mutateAsync({ path, name })}
                                />
                            </div>

                            <div className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden self-start">
                                <h3 className="px-4 py-2.5 text-sm font-semibold dark:text-white border-b border-gray-100 dark:border-gray-800">
                                    Lead que se crearía
                                </h3>
                                <LeadPreview preview={previewResp?.data} isLoading={previewLoading} />
                            </div>
                        </section>

                        {/* Envíos */}
                        <section className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                                <h3 className="text-sm font-semibold dark:text-white">Últimos envíos</h3>
                                <button
                                    onClick={() => reprocess.mutate()}
                                    disabled={reprocess.isPending}
                                    className="text-xs text-primary hover:underline disabled:opacity-50"
                                    title="Vuelve a procesar los envíos que no llegaron a crear un lead"
                                >
                                    Reprocesar los que no crearon lead
                                </button>
                            </div>

                            {submissions.length === 0 ? (
                                <p className="p-4 text-xs text-gray-400">Todavía no llegó ningún envío.</p>
                            ) : (
                                <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                    {submissions.map((s) => {
                                        const meta = STATUS_META[s.status] || STATUS_META.skipped;
                                        return (
                                            <div key={s.id} className="px-4 py-2.5 flex items-center gap-3 text-xs">
                                                <span className={`px-2 py-0.5 rounded shrink-0 ${meta.tone}`}>{meta.label}</span>
                                                <span className="text-gray-400 shrink-0">
                                                    {dayjs(s.created_at).format('DD/MM HH:mm')}
                                                </span>
                                                <span className="flex-1 min-w-0 truncate dark:text-gray-300">
                                                    {s.contact
                                                        ? (s.contact.full_name || s.contact.email)
                                                        : (s.error || '—')}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}
